import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { AwardProgressBadge } from "../../../../../common/components/AwardProgressBadge/AwardProgressBadge";
import { AWARD_PROGRESS } from "../../../../../common/enums/award-progress-codes";
import LeafTrendIndicator, { TrendEnum } from "../../../../../common/leaf/LeafTrendIndicator/LeafTrendIndicator";
import { IconTrendArrow } from "../../../../../common/icons/IconTrendArrow/IconTrendArrow";
import LeafTileDivider from "../../../../../common/leaf/LeafTileDivider/LeafTileDivider";
import defaultAwardContent from "../../../../../content/awardLabels";
import "./LegsProgress.scss";

export interface LegsProgressItem {
  label: ReactNode;
  value?: ReactNode;
  delta?: number;
  trend?: TrendEnum;
  number?: number;
  status?: AWARD_PROGRESS;
  clickable?: boolean;
  testid?: string;
}

interface Props {
  items: LegsProgressItem[];
  showDivider?: boolean;
  onLabelClick?: (item: LegsProgressItem, index: number) => void;
}

export const LegsProgress = ({ items, showDivider, onLabelClick }: Props) => {
  const { t } = useTranslation(["awardLabels"]);
  const noDataLabel = t(
    "awardLabels.no-data-available",
    defaultAwardContent.awardLabels["no-data-available"] as string,
  );

  const handleLabelClick = (item: LegsProgressItem, index: number) => {
    onLabelClick?.(item, index);
  };

  return (
    <>
      <div className="legs-progress">
        {items.map((item, index) => (
          <div key={index} className="legs-progress__item" {...(item.testid ? { "data-testid": item.testid } : {})}>
            {item.clickable && onLabelClick ? (
              <button
                className="legs-progress__label legs-progress__label--clickable"
                onClick={() => handleLabelClick(item, index)}
              >
                {item.label}
              </button>
            ) : (
              <span className="legs-progress__label">{item.label}</span>
            )}

            <span className="legs-progress__value">
              {item.status ? (
                <AwardProgressBadge status={item.status} />
              ) : (
                <span className="legs-progress__value-wrapper">
                  {item.trend !== undefined &&
                    item.number !== undefined &&
                    item.trend !== TrendEnum.EQUAL &&
                    item.number !== 0 && (
                      <LeafTrendIndicator trend={item.trend} number={item.number} arrowType="trendArrow" />
                    )}
                  {item.delta !== undefined && item.delta !== 0 && (
                    <span className={`legs-progress__delta${item.delta < 0 ? " legs-progress__delta--negative" : ""}`}>
                      <IconTrendArrow
                        direction={item.delta >= 0 ? "up" : "down"}
                        color={item.delta < 0 ? "red" : "green"}
                      />
                      {Math.abs(item.delta)}
                    </span>
                  )}
                  {item.value !== undefined && (
                    <span
                      className="legs-progress__value-text"
                      {...(item.value === "--" ? { role: "text", "aria-label": noDataLabel } : {})}
                    >
                      {item.value === "--" ? <span aria-hidden="true">{item.value}</span> : item.value}
                    </span>
                  )}
                </span>
              )}
            </span>
          </div>
        ))}
      </div>
      {showDivider && <LeafTileDivider />}
    </>
  );
};
